import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { Skeleton } from '@aitoken/ui/components/skeleton';
import { cn } from '@aitoken/ui/lib/utils';
import { useHomeT } from '@/i18n/messages/home';
import { DEMO, yuan, type DemoQuote } from '@/lib/demo-quote';
import { publicModelStatsQuery, type PublicModelStats } from '@/lib/queries';

/*
 * 首屏的成功率（docs/15）：标题里的大号百分比从低处数上来，胶片中间那一格是“每 1000 个任务”的点阵，
 * 成功的点按对角线依次亮起，失败的点是空心的（数量按成功率算）；多个模型时自动轮换，也可点选。
 * 没有开启任何模型时，退回显示一个已完成的示例任务。
 */

export type RateModel = PublicModelStats['models'][number];

const COLS = 50;
const ROWS = 20;
const ROTATE_MS = 6500;

function usePrefersReducedMotion() {
  const [reduced, setReduced] = React.useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  React.useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setReduced(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return reduced;
}

/** 首屏共用：成功率数据与当前展示的模型（多个时自动轮换；访客点选后停止轮换） */
export function useHomeRates() {
  const stats = useQuery(publicModelStatsQuery);
  const models = stats.data?.models ?? [];
  const [index, setIndex] = React.useState(0);
  const [pinned, setPinned] = React.useState(false);
  const reduced = usePrefersReducedMotion();
  React.useEffect(() => {
    if (pinned || reduced || models.length < 2) return;
    const id = window.setInterval(() => setIndex((i) => (i + 1) % models.length), ROTATE_MS);
    return () => window.clearInterval(id);
  }, [pinned, reduced, models.length]);
  const select = (i: number) => {
    setIndex(i);
    setPinned(true);
  };
  return {
    pending: stats.isPending,
    windowDays: stats.data?.window_days ?? 30,
    models,
    current: models.length ? models[index % models.length]! : null,
    index: models.length ? index % models.length : 0,
    select,
  };
}

/**
 * 多个模型名共同的前导词（如 "Seedance"）只在标签栏前显示一次，标签只留区分部分（"2.0 Fast"），
 * 四个模型也能一行放下；只有一个模型或没有共同前缀时原样显示。
 */
export function splitFamily(names: string[]): { family: string; labels: string[] } {
  if (names.length < 2) return { family: '', labels: names };
  const words = names.map((n) => n.split(' '));
  let k = 0;
  while (words.every((w) => w.length > k + 1 && w[k] === words[0]![k])) k++;
  return k ? { family: words[0]!.slice(0, k).join(' '), labels: words.map((w) => w.slice(k).join(' ')) } : { family: '', labels: names };
}

/** 从目标值往下 12 个点开始，约 1.4 秒缓出数到目标值 */
export function CountUp({ value, className }: { value: number; className?: string }) {
  const reduced = usePrefersReducedMotion();
  // 构建期预渲染（无 window）直接给最终值：爬虫读到的是真实成功率
  const [shown, setShown] = React.useState(reduced || typeof window === 'undefined' ? value : Math.max(0, value - 12));
  React.useEffect(() => {
    if (reduced) {
      setShown(value);
      return;
    }
    const from = Math.max(0, value - 12);
    const start = performance.now();
    let raf = 0;
    const step = (now: number) => {
      const p = Math.min(1, (now - start) / 1400);
      setShown(from + (value - from) * (1 - Math.pow(1 - p, 4)));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, reduced]);
  return (
    <span className={cn('tabular-nums', className)} aria-hidden>
      {shown.toFixed(1)}
    </span>
  );
}

/** 失败点的位置：按模型名生成、固定不变，避免每次渲染跳动 */
function failedCells(seed: string, count: number) {
  let h = 2166136261;
  for (const ch of seed) h = Math.imul(h ^ ch.charCodeAt(0), 16777619);
  const out = new Set<number>();
  while (out.size < count) {
    h = Math.imul(h ^ (h >>> 13), 1274126177) >>> 0;
    out.add(h % (COLS * ROWS));
  }
  return out;
}

function DotField({ model }: { model: RateModel }) {
  const failed = Math.min(COLS * ROWS, Math.round((100 - model.success_rate) * 10));
  const cells = React.useMemo(() => failedCells(model.model, failed), [model.model, failed]);
  return (
    <div className="relative overflow-hidden" aria-hidden>
      <div className="grid gap-[3px]" style={{ gridTemplateColumns: `repeat(${COLS}, minmax(0, 1fr))` }}>
        {Array.from({ length: COLS * ROWS }, (_, i) => {
          const row = Math.floor(i / COLS);
          const col = i % COLS;
          const bad = cells.has(i);
          return (
            <span
              key={i}
              className={cn(
                'rate-dot aspect-square rounded-full',
                bad ? 'rate-dot-failed bg-transparent ring-1 ring-[#e28b7f] ring-inset' : 'bg-gold',
              )}
              style={{ '--d': (col + row) * 14 } as React.CSSProperties}
            />
          );
        })}
      </div>
      <div className="rate-sweep pointer-events-none absolute inset-0" />
    </div>
  );
}

export function SuccessBoard({
  quote,
  rates,
  className,
}: {
  quote: DemoQuote;
  rates: ReturnType<typeof useHomeRates>;
  className?: string;
}) {
  const t = useHomeT();
  const tabsRef = React.useRef<HTMLDivElement>(null);
  // 窄屏标签栏可横向滚动：轮播或点选到看不见的标签时，只在标签栏内把它滚进来（不滚动页面）
  React.useEffect(() => {
    const list = tabsRef.current;
    const tab = list?.children[rates.index] as HTMLElement | undefined;
    if (!list || !tab) return;
    if (tab.offsetLeft < list.scrollLeft || tab.offsetLeft + tab.offsetWidth > list.scrollLeft + list.clientWidth) {
      list.scrollTo({ left: tab.offsetLeft - (list.clientWidth - tab.offsetWidth) / 2, behavior: 'smooth' });
    }
  }, [rates.index]);
  const frame = cn(
    'flex h-full flex-col overflow-hidden rounded-[4px] bg-timeline text-[13px] text-timeline-foreground',
    className,
  );

  if (rates.pending) return <Skeleton className={cn('h-full min-h-56 rounded-[4px] bg-timeline', className)} />;

  const m = rates.current;
  if (!m) {
    return (
      <div data-ui className={frame}>
        <div className="flex items-center justify-between gap-3 border-b border-timeline-grid px-4 py-2.5">
          <code className="min-w-0 truncate font-mono text-[13px]">{DEMO.taskId}</code>
          <span className="inline-flex h-6 shrink-0 items-center rounded-[4px] bg-gold px-2 font-medium text-ink">
            {t('board.done')}
          </span>
        </div>
        <div className="flex flex-1 items-center gap-2 px-4 py-6 text-[22px] font-medium">
          <Check className="size-5 text-gold" strokeWidth={3} aria-hidden />
          {t('board.result')}
        </div>
        <div className="border-t border-timeline-grid px-4 py-2.5 text-timeline-muted tabular-nums">
          {t('board.settled', { amount: `${quote.symbol}${yuan(quote.charged)}` })}
        </div>
      </div>
    );
  }

  const ok = Math.max(0, 1000 - Math.round((100 - m.success_rate) * 10));
  const { family, labels } = splitFamily(rates.models.map((x) => x.display_name));
  return (
    <div data-ui className={frame} role="group" aria-label={t('board.label')}>
      <div className="flex items-center justify-between gap-3 border-b border-timeline-grid px-2 py-1.5">
        <div className="flex min-w-0 items-center gap-1">
          {family ? <span className="shrink-0 pl-2.5 pr-1 font-medium text-timeline-foreground">{family}</span> : null}
          <div ref={tabsRef} className="relative flex min-w-0 gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="tablist" aria-label={t('board.models')}>
            {rates.models.map((x, i) => (
              <button
                key={x.model}
                type="button"
                role="tab"
                aria-selected={i === rates.index}
                aria-label={x.display_name}
                onClick={() => rates.select(i)}
                className={cn(
                  'press h-8 shrink-0 whitespace-nowrap rounded-[3px] px-2.5 font-medium transition-colors focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-gold',
                  i === rates.index ? 'bg-[#2e2a22] text-gold' : 'text-timeline-muted hover:text-timeline-foreground',
                )}
              >
                {labels[i]}
              </button>
            ))}
          </div>
        </div>
        <span className="shrink-0 pr-2 text-timeline-muted">{t('board.window', { days: rates.windowDays })}</span>
      </div>
      <div className="flex flex-1 flex-col justify-center gap-3 px-4 py-3">
        <DotField key={m.model} model={m} />
      </div>
      <p className="flex items-center justify-between gap-3 border-t border-timeline-grid px-4 py-2.5">
        <span className="tabular-nums">{t('board.perThousand', { ok })}</span>
        <span className="flex shrink-0 items-center gap-3 text-timeline-muted">
          <span className="flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-gold" aria-hidden />
            {t('board.legend.ok')}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="size-2 rounded-full ring-1 ring-[#e28b7f] ring-inset" aria-hidden />
            {t('board.legend.failed')}
          </span>
        </span>
      </p>
    </div>
  );
}
